import { Link } from '@tanstack/react-router';
import { SEO } from '@/components';

export function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto">
      <SEO
        title="Privacy Policy"
        description="Privacy policy for miweather.org. Learn how we collect, use, and protect your data when using our Michigan weather service."
        path="/privacy"
      />
      <Link to="/" className="text-[var(--color-info)] hover:underline text-sm mb-4 inline-block">
        ← Back to Home
      </Link>

      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

      <div className="space-y-6 text-white/80">
        <p className="text-white/60 text-sm">Last updated: January 2025</p>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Overview</h2>
          <p>
            miweather.org is a free weather service for Michigan residents. We are committed to
            protecting your privacy and being transparent about the data we collect.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Information We Collect</h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Location Data:</strong> If you grant permission, we access your device's
              location to show local weather. This data is used only to fetch weather data and is
              not stored on our servers.
            </li>
            <li>
              <strong>IP-Based Location:</strong> If you deny location permission, we may estimate
              your general location from your IP address to provide relevant weather data.
            </li>
            <li>
              <strong>Local Storage:</strong> We store your preferences (favorites, theme settings)
              in your browser's local storage. This data never leaves your device.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Data We Don't Collect</h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>We do not require account creation or collect personal information</li>
            <li>We do not sell your data for monetary consideration</li>
            <li>We do not store your precise location on our servers</li>
          </ul>
          <p className="mt-2 text-sm text-white/60">
            Note: Third-party services like Google Analytics and Google AdSense may use cookies as
            described in the Third-Party Services section below. Under certain privacy laws,
            sharing data with advertising partners may be considered "sharing" of personal
            information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Third-Party Services</h2>
          <p className="mb-2">We use the following external services:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>National Weather Service (NWS):</strong> Weather data is sourced from the
              U.S. National Weather Service API. No personal data is shared with NWS.
            </li>
            <li>
              <strong>Open-Meteo:</strong> Supplementary weather data for enhanced forecasts. No
              personal data is shared with Open-Meteo.
            </li>
            <li>
              <strong>IP-API:</strong> For approximate location when GPS is unavailable. Only your
              IP address is sent; no other data is shared.
            </li>
            <li>
              <strong>Vercel Analytics:</strong> We use Vercel Analytics to understand how visitors
              use our site. This service collects anonymized usage data including page views,
              referrer information, browser type, device type, and country. Vercel Analytics does
              not use cookies and does not collect personally identifiable information. For more
              details, see{' '}
              <a
                href="https://vercel.com/docs/analytics/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-info)] hover:underline"
              >
                Vercel's Analytics Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Google Analytics:</strong> We use Google Analytics 4 to analyze website
              traffic and usage patterns. Google Analytics uses cookies to collect information
              about how visitors use our site, including pages visited, time spent on pages, and
              referral sources. This data is used to improve our services. Google may use this
              data in accordance with their privacy policy. You can opt out of Google Analytics by
              installing the{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-info)] hover:underline"
              >
                Google Analytics Opt-out Browser Add-on
              </a>
              .
            </li>
            <li>
              <strong>Google Fonts:</strong> We use Google Fonts to display typography on our
              website. When you visit our site, your browser downloads font files from Google's
              servers, which may result in your IP address being transmitted to Google. For more
              information, see{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-info)] hover:underline"
              >
                Google's Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Google AdSense:</strong> We may display advertisements through Google
              AdSense. Google uses cookies to serve ads based on your prior visits to our website
              or other websites. You may opt out of personalized advertising by visiting{' '}
              <a
                href="https://adssettings.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-info)] hover:underline"
              >
                Google Ads Settings
              </a>
              . Note: Under privacy laws like CCPA, sharing data with Google for personalized
              advertising may constitute "sharing" of personal information. You can opt out via
              Google Ads Settings or industry opt-out tools at{' '}
              <a
                href="https://www.aboutads.info/choices"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-info)] hover:underline"
              >
                aboutads.info/choices
              </a>
              .
            </li>
            <li>
              <strong>Unsplash:</strong> We may use images from Unsplash for visual content on our
              website. When these images load, your browser connects to Unsplash's servers, which
              may log your IP address. For more information, see{' '}
              <a
                href="https://unsplash.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-info)] hover:underline"
              >
                Unsplash's Privacy Policy
              </a>
              .
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Your Choices</h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>You can deny location permission and still use the service with IP-based location</li>
            <li>You can clear your browser's local storage to remove saved preferences</li>
            <li>You can use the site without any location services (manually search for locations)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">California Privacy Rights (CCPA)</h2>
          <p className="mb-2">
            If you are a California resident, you have the following rights under the California
            Consumer Privacy Act (CCPA):
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Right to Know:</strong> You may request disclosure of the categories and
              specific pieces of personal information we have collected about you.
            </li>
            <li>
              <strong>Right to Delete:</strong> You may request deletion of your personal
              information, subject to certain exceptions.
            </li>
            <li>
              <strong>Right to Opt-Out:</strong> You have the right to opt out of the sale of your
              personal information. Note: We do not sell personal information.
            </li>
            <li>
              <strong>Right to Non-Discrimination:</strong> We will not discriminate against you
              for exercising your privacy rights.
            </li>
          </ul>
          <p className="mt-2">
            To exercise these rights, contact us at{' '}
            <a
              href="mailto:privacy@miweather.org"
              className="text-[var(--color-info)] hover:underline"
            >
              privacy@miweather.org
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">European Privacy Rights (GDPR)</h2>
          <p className="mb-2">
            If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland,
            you have the following rights under the General Data Protection Regulation (GDPR):
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Right of Access:</strong> You may request a copy of the personal data we hold
              about you.
            </li>
            <li>
              <strong>Right to Rectification:</strong> You may request correction of inaccurate
              personal data.
            </li>
            <li>
              <strong>Right to Erasure:</strong> You may request deletion of your personal data
              under certain circumstances.
            </li>
            <li>
              <strong>Right to Restrict Processing:</strong> You may request that we limit how we
              use your data.
            </li>
            <li>
              <strong>Right to Data Portability:</strong> You may request your data in a
              machine-readable format.
            </li>
            <li>
              <strong>Right to Object:</strong> You may object to our processing of your personal
              data.
            </li>
            <li>
              <strong>Right to Lodge a Complaint:</strong> You have the right to file a complaint
              with your local data protection authority.
            </li>
          </ul>
          <p className="mt-2">
            To exercise these rights, contact us at{' '}
            <a
              href="mailto:privacy@miweather.org"
              className="text-[var(--color-info)] hover:underline"
            >
              privacy@miweather.org
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Montana Privacy Rights (MTCDPA)</h2>
          <p className="mb-2">
            If you are a Montana resident, you have rights under the Montana Consumer Data Privacy
            Act (MTCDPA), effective October 1, 2024. These rights include:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Right to Access:</strong> You may confirm whether we are processing your
              personal data and access such data.
            </li>
            <li>
              <strong>Right to Correct:</strong> You may request correction of inaccurate personal
              data.
            </li>
            <li>
              <strong>Right to Delete:</strong> You may request deletion of your personal data.
            </li>
            <li>
              <strong>Right to Data Portability:</strong> You may obtain a copy of your personal
              data in a portable format.
            </li>
            <li>
              <strong>Right to Opt-Out:</strong> You may opt out of the processing of personal data
              for targeted advertising, sale of personal data, or profiling. Note: We do not engage
              in these activities.
            </li>
          </ul>
          <p className="mt-2">
            To exercise these rights, contact us at{' '}
            <a
              href="mailto:privacy@miweather.org"
              className="text-[var(--color-info)] hover:underline"
            >
              privacy@miweather.org
            </a>
            . If we decline your request, you may appeal by contacting us at the same address.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Children's Privacy</h2>
          <p>
            miweather.org is intended for users who are at least 18 years of age. We do not
            knowingly collect personal information from anyone under 18. If you are under 18, please
            do not use this service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Data Retention</h2>
          <p>
            We do not retain personal data on our servers. Location data is used only to fetch
            weather information and is not stored. Your preferences (favorites, theme settings) are
            stored only in your browser's local storage on your device. Anonymized analytics data
            collected by Vercel Analytics is retained according to Vercel's data retention policies.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">International Data Transfers</h2>
          <p>
            Our services are hosted in the United States. If you access miweather.org from outside
            the United States, your information may be transferred to, stored, and processed in the
            United States, where data protection laws may differ from those in your country. By
            using our services, you consent to the transfer of your information to the United
            States.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Contact</h2>
          <p>
            For questions about this privacy policy, please contact us at{' '}
            <a
              href="mailto:privacy@miweather.org"
              className="text-[var(--color-info)] hover:underline"
            >
              privacy@miweather.org
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. Any changes will be posted on this
            page with an updated revision date.
          </p>
        </section>

        <section className="pt-4 border-t border-white/10">
          <p className="text-white/60">
            See also: <Link to="/terms" className="text-[var(--color-info)] hover:underline">Terms of Use</Link>
          </p>
        </section>
      </div>
    </div>
  );
}
